import {normalizeProvider, getPosProvider} from "../../pos/providers";
import { mcGetItem, mcSetItem } from "./mcCloudSync";
import { buildPreparedData, displayProductNameFromRow } from "./mcEngineData";
import { formatStoreLabel, normalizeStoreValue } from "./profileAccess";

const POS_SETTINGS_KEY = "mc-pos-integration-settings";
const PREPARED_INVENTORY_KEY = "mc-prepared-inventory";

export const POS_ENGINE_HEADERS = [
  "id",
  "name",
  "product",
  "sku",
  "product_category",
  "category",
  "retail_price",
  "retailPrice",
  "price",
  "display_retail_price",
  "supply_price",
  "supplyPrice",
  "cost_price",
  "costPrice",
  "average_cost",
  "avg_cost",
  "unit_cost",
  "attributed_cost",
  "cost",
  "system_soh",
  "systemSoh",
  "inventory_Main_Outlet",
  "tags",
  "source",
  "synced_at",
];

export const DEFAULT_POS_SETTINGS = {
  dataSourceMode: "csv",
  provider: "lightspeed",
  providerLabel: "Lightspeed",
  status: "not_connected",
  lastTestedAt: "",
  lastSyncedAt: "",
  notes: "Tokens are not stored in MC. Live sync will run through Firebase Functions once API access is ready.",
  outletMappings: {},
  fieldMapping: {
    product: "name",
    sku: "sku",
    category: "product_category",
    cost: "retail_price",
    systemSoh: "inventory_Main_Outlet",
  },
};

function normalizeScope(scope = "ALL") {
  const value = normalizeStoreValue(scope || "ALL");
  return !value || value === "ALL" ? "ALL" : value;
}

function scopedKey(baseKey, scope = "ALL") {
  const normalized = normalizeScope(scope);
  return normalized === "ALL" ? baseKey : `${baseKey}:${normalized}`;
}

function readJson(key, fallback) {
  try {
    const raw = mcGetItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
}

export function loadPosSettings(scope = "ALL") {
  let allSettings = readJson(scopedKey(POS_SETTINGS_KEY, "ALL"), null);
  const scopedSettings = readJson(scopedKey(POS_SETTINGS_KEY, scope), null);
  if (scopedSettings?.provider && allSettings?.provider && normalizeProvider(scopedSettings.provider) !== normalizeProvider(allSettings.provider)) allSettings = null;
  return {
    ...DEFAULT_POS_SETTINGS,
    ...(allSettings || {}),
    ...(scopedSettings || {}),
    fieldMapping: {
      ...DEFAULT_POS_SETTINGS.fieldMapping,
      ...(allSettings?.fieldMapping || {}),
      ...(scopedSettings?.fieldMapping || {}),
    },
    outletMappings: {
      ...(allSettings?.outletMappings || {}),
      ...(scopedSettings?.outletMappings || {}),
    },
  };
}

export function savePosSettings(settings = {}, scope = "ALL") {
  const current = loadPosSettings(scope);
  const changedProvider = settings.provider !== undefined && normalizeProvider(settings.provider) !== normalizeProvider(current.provider);
  if (changedProvider) settings = {...settings, provider:normalizeProvider(settings.provider), providerLabel:getPosProvider(settings.provider).label,
    status:"not_connected",lastTestedAt:"",lastSyncedAt:"",outletMappings:{},fieldMapping:{...DEFAULT_POS_SETTINGS.fieldMapping},syncFilters:{}};
  const next = {
    ...current,
    ...settings,
    fieldMapping: { ...current.fieldMapping, ...(settings.fieldMapping || {}) },
    outletMappings: changedProvider ? {} : { ...current.outletMappings, ...(settings.outletMappings || {}) },
    updatedAt: new Date().toISOString(),
  };
  mcSetItem(scopedKey(POS_SETTINGS_KEY, scope), JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("mc-pos-settings-change", { detail: { scope: normalizeScope(scope), settings: next } }));
  }
  return next;
}

export function loadPreparedInventory(scope = "ALL") {
  const payload = readJson(scopedKey(PREPARED_INVENTORY_KEY, scope), { rows: [], syncedAt: "", source: "pos" });
  return { rows: Array.isArray(payload?.rows) ? payload.rows : [], syncedAt: payload?.syncedAt || "", source: payload?.source || "pos" };
}

function publishLinkInventorySnapshot(payload = {}, scope = "ALL") {
  const siteId = normalizeScope(scope);
  const snapshot = {
    ...payload,
    source: payload.source || "morpheus-prepared-inventory",
    authority: "morpheus",
    siteId,
    siteName: payload.siteName || formatStoreLabel(scope || "ALL"),
    syncedAt: payload.syncedAt || new Date().toISOString(),
    rows: (payload.rows || []).map((row, index) => ({
      id: String(row.id || row.sku || `${siteId}-${index + 1}`),
      sku: String(row.sku || row.variant_sku || row.code || "").trim(),
      productName: String(row.productName || row.product || row.name || "").trim(),
      category: String(row.category || row.product_category || "").trim(),
      physicalSoh: Number(row.systemSoh ?? row.system_soh ?? row.realSoh ?? row.real_soh ?? row.soh ?? 0) || 0,
      retailPrice: Number(String(row.retailPrice ?? row.retail_price ?? row.price ?? 0).replace(/[^0-9.-]/g, "")) || 0,
      cost: Number(String(row.cost ?? row.supplyPrice ?? row.supply_price ?? 0).replace(/[^0-9.-]/g, "")) || 0,
      source: payload.source || "morpheus-prepared-inventory",
      provider: payload.provider || "lightspeed",
      syncedAt: payload.syncedAt || new Date().toISOString(),
    })).filter((row) => row.productName),
  };
  mcSetItem(scopedKey("mc-link-inventory-snapshot", scope), JSON.stringify(snapshot));
  return snapshot;
}

export function savePreparedInventory(rows = [], scope = "ALL", meta = {}) {
  const payload = {
    rows: Array.isArray(rows) ? rows : [],
    source: meta.source || "pos-placeholder",
    provider: meta.provider || "lightspeed",
    siteId: normalizeScope(scope),
    siteName: formatStoreLabel(scope || "ALL"),
    syncedAt: meta.syncedAt || new Date().toISOString(),
  };
  mcSetItem(scopedKey(PREPARED_INVENTORY_KEY, scope), JSON.stringify(payload));
  publishLinkInventorySnapshot(payload, scope);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("mc-prepared-inventory-change", { detail: payload }));
  }
  return payload;
}

function firstNumericField(row = {}, keys = []) {
  for (const key of keys) {
    if (!key) continue;
    const value = row?.[key];
    if (value === undefined || value === null || value === "") continue;
    const n = Number(String(value).replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function normalizeFieldToken(value = "") {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function normalizeScopeToken(value = "") {
  return normalizeFieldToken(formatStoreLabel(value || "").replace(/\b(store|site|outlet)\b/gi, ""));
}

function numericValue(value) {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function nestedNumericCandidate(row = {}, aliases = [], options = {}) {
  const aliasTokens = new Set((aliases || []).map(normalizeFieldToken).filter(Boolean));
  const scopeToken = normalizeScopeToken(options.scope || "");
  const kind = options.kind || "generic";
  const candidates = [];
  const seen = new Set();

  const walk = (value, path = [], depth = 0) => {
    if (depth > 7 || value === null || value === undefined) return;
    if (typeof value !== "object") return;
    if (seen.has(value)) return;
    seen.add(value);

    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, [...path, String(index)], depth + 1));
      return;
    }

    const objectContext = Object.values(value)
      .filter((item) => ["string", "number"].includes(typeof item))
      .slice(0, 8)
      .join(" ");
    Object.entries(value).forEach(([key, child]) => {
      const keyToken = normalizeFieldToken(key);
      const nextPath = [...path, key];
      const pathToken = normalizeFieldToken(`${nextPath.join(" ")} ${objectContext}`);
      const number = numericValue(child);
      if (number !== null && aliasTokens.has(keyToken)) {
        let score = 100 - depth;
        if (scopeToken && pathToken.includes(scopeToken)) score += 80;
        if (kind === "soh" && /(inventory|stock|outlet|location|available|onhand)/.test(pathToken)) score += 35;
        if (kind === "price" && /(retail|sell|pricing|pricebook|default)/.test(pathToken)) score += 35;
        if (kind === "cost" && /(supply|cost|average|attributed)/.test(pathToken)) score += 35;
        candidates.push({ number, score, path: nextPath.join(".") });
      }
      if (child && typeof child === "object") walk(child, nextPath, depth + 1);
    });
  };

  walk(row);
  candidates.sort((a, b) => b.score - a.score);
  return candidates[0] || null;
}

export function normalizeInventoryRows(rows = [], mapping = {}, meta = {}) {
  const syncedAt = meta.syncedAt || new Date().toISOString();
  const fieldMapping = {
    ...DEFAULT_POS_SETTINGS.fieldMapping,
    ...(mapping || {}),
  };

  const pick = (row, key, fallbacks = []) => {
    const candidates = [fieldMapping[key], ...fallbacks].filter(Boolean);
    for (const candidate of candidates) {
      const value = row?.[candidate];
      if (value !== undefined && value !== null && String(value).trim() !== "") return value;
    }
    return "";
  };

  const numberPick = (row, key, fallbacks = [], options = {}) => {
    const candidates = [fieldMapping[key], ...fallbacks].filter(Boolean);
    let direct = null;
    for (const candidate of candidates) {
      const value = row?.[candidate];
      if (value === undefined || value === null || value === "") continue;
      const n = numericValue(value);
      if (n === null) continue;
      direct = { value: n, source: candidate, recovered: false };
      // A non-zero canonical field is authoritative. Zero remains provisional:
      // some POS adapters emit flattened zero placeholders while retaining the
      // real new-product value inside nested price/inventory records.
      if (n !== 0) return direct;
      break;
    }
    const recovered = nestedNumericCandidate(row, candidates, {
      scope: meta.scope || "",
      kind: options.kind || "generic",
    });
    if (recovered && (recovered.number !== 0 || !direct)) {
      return { value: recovered.number, source: recovered.path, recovered: true };
    }
    return direct || { value: 0, source: "", recovered: false };
  };

  return (rows || [])
    .map((row, index) => {
      const product = displayProductNameFromRow(row, {
        product: fieldMapping.product || "name",
        sku: fieldMapping.sku || "sku",
        category: fieldMapping.category || "product_category",
        cost: fieldMapping.cost || "retail_price",
        systemSoh: fieldMapping.systemSoh || "inventory_Main_Outlet",
      });
      const sku = pick(row, "sku", ["sku", "variant_sku", "composite_sku", "code", "barcode"]);
      const category = pick(row, "category", ["product_category", "category"]);
      const retailPriceResult = numberPick(row, "retail_price", ["retail_price", "retailPrice", "price", "display_retail_price", "default_price", "price_excluding_tax", "price_including_tax", "sell_price", "selling_price", "unit_price", "amount", "value"], { kind: "price" });
      const supplyPriceResult = numberPick(row, "supply_price", ["supply_price", "supplyPrice", "cost_price", "costPrice", "average_cost", "avg_cost", "unit_cost", "attributed_cost", "supplyCost"], { kind: "cost" });
      const costResult = numberPick(row, "cost", [fieldMapping.cost, "retail_price", "retailPrice", "price", "cost", "supply_price", "supplyPrice", "sell_price", "selling_price"], { kind: "price" });
      const systemSohResult = numberPick(row, "systemSoh", [fieldMapping.systemSoh, "inventory_Main_Outlet", "inventory_main_outlet", "system_soh", "systemSoh", "stock_on_hand", "stockOnHand", "quantity_on_hand", "quantityOnHand", "on_hand", "onHand", "available_to_sell", "availableToSell", "current_amount", "currentAmount", "inventory_level", "inventoryLevel", "soh", "inventory", "quantity", "available", "count", "balance"], { kind: "soh" });
      const retailPrice = retailPriceResult.value;
      const supplyPrice = supplyPriceResult.value;
      const cost = costResult.value || retailPrice || supplyPrice || 0;
      const systemSoh = systemSohResult.value;
      const recoveredFields = {};
      if (retailPriceResult.recovered) recoveredFields.retail_price = retailPriceResult.source;
      if (supplyPriceResult.recovered) recoveredFields.supply_price = supplyPriceResult.source;
      if (costResult.recovered) recoveredFields.cost = costResult.source;
      if (systemSohResult.recovered) recoveredFields.system_soh = systemSohResult.source;
      return {
        ...(row || {}),
        id: row?.id || sku || `pos-row-${index + 1}`,
        product: String(product || row?.product || row?.name || "").trim(),
        name: String(product || row?.name || row?.product || "").trim(),
        sku: String(sku || "").trim(),
        category: String(category || "").trim(),
        product_category: String(category || row?.product_category || "").trim(),
        cost,
        retail_price: retailPrice || 0,
        supply_price: supplyPrice || 0,
        systemSoh,
        system_soh: systemSoh,
        inventory_Main_Outlet: systemSoh,
        source: meta.source || row?.source || "pos-placeholder",
        provider: meta.provider || row?.provider || "lightspeed",
        syncedAt: syncedAt,
        synced_at: syncedAt,
        ...(Object.keys(recoveredFields).length ? { valueRecovery: recoveredFields } : {}),
      };
    })
    .filter((row) => row.product);
}

export function preparedInventoryToEngineRows(rows = []) {
  const normalizedRows = (rows || []).map((row, index) => ({
    ...(row || {}),
    id: row.id || row.sku || `pos-${index + 1}`,
    name: row.name || row.product || "",
    product: row.product || row.name || "",
    sku: row.sku || "",
    product_category: row.product_category || row.category || "",
    category: row.category || row.product_category || "",
    retail_price: row.retail_price ?? row.retailPrice ?? row.price ?? row.cost ?? 0,
    supply_price: row.supply_price ?? row.supplyPrice ?? row.cost_price ?? row.costPrice ?? row.average_cost ?? row.attributed_cost ?? 0,
    cost: row.cost ?? row.retail_price ?? row.retailPrice ?? row.price ?? row.supply_price ?? 0,
    system_soh: row.system_soh ?? row.systemSoh ?? row.inventory_Main_Outlet ?? 0,
    systemSoh: row.systemSoh ?? row.system_soh ?? row.inventory_Main_Outlet ?? 0,
    inventory_Main_Outlet: row.inventory_Main_Outlet ?? row.systemSoh ?? row.system_soh ?? 0,
    source: row.source || "pos",
    synced_at: row.synced_at || row.syncedAt || "",
    tags: Array.isArray(row.tags) ? row.tags.join(", ") : (row.tags || ""),
  }));

  const headerSet = new Set(POS_ENGINE_HEADERS);
  normalizedRows.forEach((row) => Object.keys(row || {}).forEach((key) => headerSet.add(key)));
  normalizedRows.__headers = Array.from(headerSet);
  return normalizedRows;
}

export function buildEngineFromPreparedInventory(engine = {}, preparedInventoryRows = [], options = {}) {
  const rawRows = preparedInventoryToEngineRows(preparedInventoryRows);
  const posSettings = loadPosSettings(options.scope || "ALL");
  const posMapping = {
    ...DEFAULT_POS_SETTINGS.fieldMapping,
    ...(posSettings.fieldMapping || {}),
    ...(options.posFieldMapping || {}),
  };
  const csvMapping = engine.csvMapping || (String(engine.config?.dataSourceMode || "csv").toLowerCase() === "pos" ? engine.csvMapping : engine.mapping) || {};
  const config = {
    ...(engine.config || {}),
    dataSourceMode: "pos",
    posProvider: options.provider || engine.config?.posProvider || "lightspeed",
    storeName: options.scope && normalizeScope(options.scope) !== "ALL" ? normalizeScope(options.scope) : (engine.config?.storeName || ""),
  };
  const headers = Array.isArray(rawRows.__headers) ? rawRows.__headers : POS_ENGINE_HEADERS;
  const next = {
    ...engine,
    csvText: "",
    parsedHeaders: headers,
    rawRows,
    mapping: posMapping,
    csvMapping,
    posFieldMapping: posMapping,
    config,
    preparedRows: buildPreparedData(rawRows, posMapping, config, engine.rules),
    fileName: `${String(options.provider || "Lightspeed").replace(/^./, (c) => c.toUpperCase())} live SOH`,
    lastGeneratedAt: options.syncedAt || new Date().toISOString(),
    uploadedAt: options.syncedAt || new Date().toISOString(),
    lastUploadedAt: options.syncedAt || new Date().toISOString(),
  };
  return next;
}
